import React, { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Image from 'components/Common/Image'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import styled from 'styled-components'
import * as Analytics from 'analytics/analytics'
import { rem } from 'polished'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import Clickable from 'components/Common/Clickable'
import type { DestinationLandingPageType } from '../DestinationLandingPage/DestinationLandingPageUtils'
import interactiveFocusStyle from 'styles/utils/interactiveFocusStyle'
import Sheet from 'components/utils/Sheet'
import { useLocation } from 'react-router'
import { useQsQueryParams } from 'hooks/useQueryParams'
import qs from 'qs'

const DestinationImage = styled(Image)`
  border-radius: ${props => props.theme.borderRadius.S};
`

const LocationCard = styled(Clickable)`
  display: grid;
  grid-template-rows: ${rem(80)} 1fr;
  width: ${rem(220)};
  transition: background-color 0.2s;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  
  ${mediaHoverable} {
    &:hover {
      background-color: ${props => props.theme.palette.neutral.default.seven};
    }
  }

  &:focus {
    ${interactiveFocusStyle}
  }
`

interface Props {
  specificPlaces: Array<App.SpecificPlace>
  isDestinationLandingPage?: boolean
  pageTypeForDestinationLandingPage?: DestinationLandingPageType
  holidayTypeForDestinationLandingPage?: string
  destinationName?: string
  showTitle?: boolean
  className?: string
  arrowsOnlyOnHover?: boolean
}

function HotelBroadSearchLocation({
  specificPlaces,
  destinationName,
  showTitle = true,
  arrowsOnlyOnHover = false,
  className,
  isDestinationLandingPage,
  pageTypeForDestinationLandingPage,
  holidayTypeForDestinationLandingPage,
}: Props) {
  const intl = useIntl()
  const location = useLocation()
  const params = useQsQueryParams<any>()

  const onClick = useCallback<React.MouseEventHandler<HTMLAnchorElement>>((e) => {
    if (!isDestinationLandingPage) {
      const placeId = e.currentTarget.dataset.placeid
      const place = specificPlaces.find(place => place.placeId === placeId)

      if (place) {
        Analytics.trackClientEvent({
          subject: `${place.placeId}-${place.name}`,
          action: 'clicked',
          category: 'broad_search_location',
          type: 'interaction',
        })
      }
    }
  }, [specificPlaces, isDestinationLandingPage])

  const destinations = useMemo(() => {
    return specificPlaces.map((destination) => {
      let newPath
      if (isDestinationLandingPage) {
        if (pageTypeForDestinationLandingPage && destination.destinationLandingPageSlug) {
          newPath = `/${pageTypeForDestinationLandingPage}/${destination.destinationLandingPageSlug}${holidayTypeForDestinationLandingPage ? `/${holidayTypeForDestinationLandingPage}` : ''}`
        }
      } else {
        const nextSearch = qs.stringify({
          ...params,
          destinationId: destination.placeId,
          destinationName: destination.name,
        })
        newPath = `${location.pathname}?${nextSearch}`
      }

      return {
        place: destination,
        to: newPath,
      }
    })
  }, [holidayTypeForDestinationLandingPage, isDestinationLandingPage, location.pathname, pageTypeForDestinationLandingPage, params, specificPlaces])

  const titleText = intl.formatMessage({
    id: 'cq2h5HGFA3',
    defaultMessage: 'Explore top destinations in {destinationName}',
    description: 'Title for the carousel showing top destinations in a searched area',
  }, { destinationName })

  return (<Group className={className} direction="vertical" gap={8}>
    {showTitle &&
      <BodyTextBlock variant="large">
        {titleText}
      </BodyTextBlock>}
    <CardCarousel gap={12} arrowsOnlyOnHover={arrowsOnlyOnHover}>
      {destinations.map((destination) => {
        return (
          <LocationCard
            key={destination.place.placeId}
            to={destination.to}
            data-placeid={destination.place.placeId}
            onClick={onClick}
          >
            <DestinationImage
              width={220}
              height={80}
              dpr={2}
              id={destination.place.image}
              alt={destination.place.name}
              fit="center"
            />
            <Sheet padding={4}>
              <BodyText variant="medium" weight="bold">{destination.place.name}</BodyText>
              <Caption colour="neutral-three" variant="medium">{destination.place.caption}</Caption>
            </Sheet>
          </LocationCard>
        ) })}
    </CardCarousel>
  </Group>)
}

export default HotelBroadSearchLocation
